import React from 'react';
import PropTypes from 'prop-types';
import connectToStores from 'alt-utils/lib/connectToStores';
import pick from 'lodash/pick';
import noop from 'lodash/noop';
import AdminFilterWordProfileActions from 'actions/admin/AdminFilterWordProfileActions';
import AdminFilterWordProfileStore from 'stores/admin/AdminFilterWordProfileStore';
import AuthStore from 'stores/AuthStore';
import TopicList from 'components/admin/topics/TopicList';
import DepartmentList from 'components/admin/topics/DepartmentList';
import './TopicsPage.styl';
import AdminFilterWordProfilePageActions from 'actions/admin/AdminFilterWordProfilePageActions';
import AdminFilterWordProfilePageStore from 'stores/admin/AdminFilterWordProfilePageStore';
import {unsavedMessage} from 'utils/const';
import TabPanel from 'components/coaching/widgets/TabPanel';
import {getQuery} from 'utils/getQueryParam';

/**
 * Topics and filter words admin page. Contains a hierarchical display of departments -> teams and
 * profiles, plus a topic editor. Supports read-only mode for non-admins.
 */
@connectToStores
export default class TopicsPage extends React.PureComponent {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  static getStores() {
    return [AdminFilterWordProfileStore, AdminFilterWordProfilePageStore, AuthStore];
  }

  static getPropsFromStores() {
    const state = AdminFilterWordProfileStore.getState();
    const pageState = AdminFilterWordProfilePageStore.getState();
    const loading = state.profilesInAccountLoading || state.departmentsLoading;
    const error = state.profilesInAccountError || state.departmentsError;
    return {
      // Threaded through most components in the page. True => you can edit, false => you can only view.
      editable: AuthStore.getState().user.isAdmin(),
      error,
      loading,
      ...pick(state, [
        'departments',
        'idToProfileMap',
        'profileDepartmentAndTeamUsage',
        'profilesInAccount',
        'topicsInAccount',
      ]),
      ...pick(pageState, [
        'expandedDepartmentIds',
        'expandedTeamIds',
        'profileEditorKey',
        'profileEditorDirty',
      ]),
    };
  }

  constructor(props) {
    super(props);
    this.state = {
      removeLeaveHook: noop,
    };
    this.beforeLeaving = this.beforeLeaving.bind(this);
  }

  beforeLeaving() {
    const {profileEditorDirty} = this.props;
    // TODO -- there has to be a better way. Alert box is ugly.
    if (profileEditorDirty) {
      return unsavedMessage;
    }
  }

  componentDidMount() {
    // Allow query param to open a specific department.
    const {openDepartmentId} = getQuery(this.props.location);
    if (openDepartmentId) {
      AdminFilterWordProfilePageActions.expandOnlyOneDepartment.defer(openDepartmentId);
    }
    // Defers to avoid invariant violation on redirect after topic delete.
    AdminFilterWordProfileActions.fetchAccountProfiles.defer();
    AdminFilterWordProfileActions.fetchDepartments.defer();

    // https://stackoverflow.com/a/39949955
    // and
    // https://github.com/ReactTraining/react-router/blob/v2.0.1/examples/confirming-navigation
    this.setState({
      removeLeaveHook: this.context.router.setRouteLeaveHook(this.props.route, this.beforeLeaving),
    });
  }

  componentWillUnmount() {
    this.state.removeLeaveHook();
    // Clear editor state.
    AdminFilterWordProfilePageActions.stopProfileEditor.defer();
  }

  render() {
    // If the user's navigating to a specific department, we need to open the departments tab for them.
    const {openDepartmentId} = getQuery(this.props.location);
    const defaultSelectedIdx = openDepartmentId && 1;
    return (
      <div className="admin-topics-page">
        <h1>Topic/Keyword Configuration</h1>
        <p>Manage your list of topics, associations with departments, and usage in Smart Alerts.</p>
        <TabPanel
          tabs={['Topics', 'Departments & Teams']}
          defaultSelectedIdx={defaultSelectedIdx}
          panels={[<TopicList {...this.props} />, <DepartmentList {...this.props} />]}
        />
      </div>
    );
  }
}
